import { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTheme, type Theme } from '../../hooks/useTheme'

const swatchColors: Record<Theme, string> = {
  electric: '#00d4ff',
  sunset: '#f97316',
  emerald: '#10b981',
  amber: '#f59e0b',
  rose: '#f43f5e',
  neon: '#39ff14',
  coral: '#fb7185',
  forest: '#22c55e',
}

const themeLabels: Record<Theme, string> = {
  electric: 'Electric',
  sunset: 'Sunset',
  rose: 'Rose',
  neon: 'Neon',
  coral: 'Coral',
  forest: 'Forest',
}

export default function ThemeSwitcher() {
  const [isHovering, setIsHovering] = useState(false)
  const { theme, setTheme, themes } = useTheme()
  const hideTimer = useRef<ReturnType<typeof setTimeout>>()

  const show = () => {
    clearTimeout(hideTimer.current)
    setIsHovering(true)
  }

  const hide = () => {
    hideTimer.current = setTimeout(() => setIsHovering(false), 200)
  }

  return (
    <div
      className="fixed top-20 right-4 md:right-8 z-50"
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      <div className="flex flex-row-reverse items-center gap-3">
        {/* Color dot button */}
        <div
          className="relative w-11 h-11 rounded-full bg-gray-950/80 backdrop-blur-sm border border-white/10 flex items-center justify-center flex-shrink-0"
        >
          <motion.div
            className="absolute inset-0 rounded-full"
            animate={{
              boxShadow: [
                `0 0 0px ${swatchColors[theme]}00`,
                `0 0 16px ${swatchColors[theme]}44`,
                `0 0 0px ${swatchColors[theme]}00`,
              ],
            }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />
          <div
            className="w-5 h-5 rounded-full relative z-10"
            style={{ backgroundColor: swatchColors[theme] }}
          />
        </div>

        {/* Animated label */}
        <motion.div
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-950/60 backdrop-blur-sm border border-white/5"
        >
          <motion.span
            className="text-[10px] font-semibold uppercase tracking-[0.15em]"
            style={{
              background: `linear-gradient(90deg, var(--electric-blue), var(--electric-purple), var(--electric-blue))`,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
            animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
          >
            ✦ Theme
          </motion.span>
        </motion.div>
      </div>

      {/* Palette popup */}
      <AnimatePresence>
        {isHovering && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.92 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="absolute right-0 top-full mt-3 flex items-center gap-2.5 px-4 py-3 rounded-2xl bg-gray-950/80 backdrop-blur-xl border border-white/10 shadow-2xl"
          >
            {themes.map((t, i) => (
              <motion.button
                key={t}
                initial={{ opacity: 0, y: -8, scale: 0.6 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.6 }}
                transition={{
                  delay: i * 0.05,
                  duration: 0.3,
                  ease: 'easeOut',
                }}
                onClick={() => {
                  setTheme(t)
                }}
                className="group relative shrink-0"
                title={themeLabels[t]}
              >
                <div
                  className="w-9 h-9 rounded-full border-2 transition-all duration-300 hover:scale-125 hover:shadow-lg"
                  style={{
                    backgroundColor: swatchColors[t],
                    borderColor: t === theme ? '#fff' : 'transparent',
                    boxShadow: t === theme ? `0 0 14px ${swatchColors[t]}66` : 'none',
                  }}
                />
                {t === theme && (
                  <svg
                    className="absolute inset-0 w-full h-full p-2 text-white"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={3}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
                <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded bg-gray-950/90 border border-white/10 text-[8px] font-semibold text-white/70 uppercase tracking-wider whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  {themeLabels[t]}
                </span>
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
